import { useState, useEffect, useCallback } from 'react';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { setPendingAutoReload } from '../lib/versionCheck';

export default function UpdateBanner() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [updateReady, setUpdateReady] = useState(false);
  const [deferredMessage, setDeferredMessage] = useState('');

  useEffect(() => {
    function handleUpdate(e) {
      setWaitingWorker(e.detail?.worker || null);
      setUpdateReady(true);
      setPendingAutoReload();
    }
    window.addEventListener('sw-update-available', handleUpdate);
    return () => window.removeEventListener('sw-update-available', handleUpdate);
  }, []);

  const handleRefresh = useCallback(() => {
    // Part 1c: check for pending scores before reloading
    let hasPending = false;
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('foretour:pendingScores:')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            const ops = parsed?.ops || parsed?.pendingOps || [];
            if (Array.isArray(ops) && ops.length > 0) { hasPending = true; break; }
          }
        }
      }
    } catch {}

    if (hasPending) {
      setDeferredMessage('Will update after your scores save');
      // Retry after 2s — scores typically flush within 1-2s
      setTimeout(() => {
        setDeferredMessage('');
        handleRefresh();
      }, 2000);
      return;
    }

    // Mark this bundle as "reloaded for" to prevent banner re-appearing
    try {
      const scripts = document.querySelectorAll('script[src]');
      for (const s of scripts) {
        const src = s.getAttribute('src') || '';
        if (/\/static\/js\/main\.[a-f0-9]+\.js/i.test(src)) {
          sessionStorage.setItem('ft_reloaded_for_bundle', src);
          break;
        }
      }
    } catch {}

    if (waitingWorker && typeof waitingWorker.postMessage === 'function') {
      waitingWorker.postMessage('skipWaiting');
      // controllerchange in versionCheck.js will reload.
      // Fallback if controllerchange doesn't fire within 2s.
      setTimeout(() => {
        if (!window.__sw_reloaded) {
          window.__sw_reloaded = true;
          window.location.reload();
        }
      }, 2000);
    } else {
      // No SW waiting (bundle-hash path) — just reload
      window.location.reload();
    }
  }, [waitingWorker]);

  if (!updateReady) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      data-testid="update-banner"
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-3 bg-[#1B4332] text-white px-4 py-3 shadow-lg cursor-pointer"
      onClick={handleRefresh}
    >
      <ArrowsClockwise className="h-5 w-5 flex-shrink-0 animate-spin" style={{ animationDuration: '3s' }} />
      <span className="text-sm font-semibold">{deferredMessage || 'New version available, tap to refresh'}</span>
    </div>
  );
}
